import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatBalance, formatCurrency } from "@/features/billing/billing-format";
import type { BillingPlansResponse } from "@/types";
import CreditBalance from "./CreditBalance";

/** Catalog id of the gated QA purchase; excluded from the "best value" badge. */
const TEST_ITEM_ID = "test-item";

interface PrepaidBillingViewProps {
  isAdminTopUpMode: boolean;
  showPrepaid: boolean;
  showTestItem?: boolean;
  /** Billing catalog; the credit packs come from `oneTimePurchases`. */
  plans?: BillingPlansResponse | null;
  /** Start Stripe checkout for a one-time credit pack. */
  onPurchase?: (purchaseId: string, purchaseName?: string) => void;
  /** Catalog id currently redirecting to checkout, if any. */
  checkoutLoading?: string | null;
}

export function PrepaidBillingView(
  {
    isAdminTopUpMode,
    showPrepaid,
    showTestItem = false,
    plans = null,
    onPurchase,
    checkoutLoading = null,
  }: PrepaidBillingViewProps,
) {
  const { t } = useTranslation(["billing"]);

  if (!showPrepaid && !isAdminTopUpMode && !showTestItem) {
    return null;
  }

  // The server already filters what appears here (the three PLN packs always;
  // the gated test item only for the designated tester), so we render whatever
  // the catalog returns.
  const packs = plans?.oneTimePurchases ?? [];

  // Highlight the pack with the lowest price-per-credit as the best value, so the
  // larger packs earn their higher sticker price. The QA test item is excluded —
  // it is a fixed 1-credit artifact, not a real value proposition.
  const eligible = packs.filter((p) => p.id !== TEST_ITEM_ID && p.credits > 0);
  const bestValueId = eligible.length > 1
    ? eligible.reduce((best, p) =>
      p.amount / p.credits < best.amount / best.credits ? p : best
    ).id
    : null;

  return (
    <>
      <CreditBalance />

      {isAdminTopUpMode && (
        <div className="rounded-lg border border-[color:color-mix(in_srgb,var(--info)_35%,transparent)] bg-[color:color-mix(in_srgb,var(--info)_16%,var(--card))] p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,var(--info)_18%,var(--card))]">
              <svg
                className="h-5 w-5 text-info"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-info">
                {t("billing:adminTopUp.title")}
              </h4>
              <p className="text-sm text-info">
                {t("billing:adminTopUp.description")}
              </p>
            </div>
          </div>
        </div>
      )}

      {packs.length > 0 && onPurchase && (
        <div className="space-y-4" data-test="credit-packs">
          <div>
            <h3 className="text-md font-medium text-foreground">
              {t("billing:packs.sectionTitle")}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t("billing:packs.creditHelper")}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((purchase) => {
              // i18n copy is keyed by catalog id, NOT purchase.name (which is
              // neutral English and must never surface to users).
              const name = t(`billing:packs.items.${purchase.id}.name`, {
                defaultValue: purchase.name,
              });
              const currency = purchase.currency.toUpperCase();
              const isBestValue = purchase.id === bestValueId;
              return (
                <div
                  key={purchase.id}
                  className={cn(
                    "relative flex flex-col rounded-xl border bg-card p-5 transition-colors",
                    isBestValue
                      ? "border-[color:color-mix(in_srgb,var(--primary)_55%,var(--border))] ring-1 ring-[color:color-mix(in_srgb,var(--primary)_30%,transparent)]"
                      : "border-border hover:border-[color:color-mix(in_srgb,var(--primary)_45%,var(--border))]",
                  )}
                  data-test={`credit-pack-${purchase.id}`}
                >
                  {isBestValue && (
                    <span
                      className="absolute right-4 top-4 rounded-full bg-[color:color-mix(in_srgb,var(--primary)_16%,var(--card))] px-2.5 py-0.5 text-xs font-medium text-[var(--primary)]"
                      data-test={`credit-pack-badge-${purchase.id}`}
                    >
                      {t("billing:packs.bestValue")}
                    </span>
                  )}
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-4xl font-semibold tracking-tight text-foreground tabular-nums">
                      {formatBalance(purchase.credits)}
                    </span>
                    <span className="text-sm font-medium text-muted-foreground">
                      {t("billing:packs.creditsUnit", {
                        count: purchase.credits,
                      })}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-lg font-semibold text-foreground tabular-nums">
                      {formatCurrency(purchase.amount, currency)}
                    </span>
                    {purchase.credits > 0 && (
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {t("billing:packs.perCredit", {
                          price: formatCurrency(
                            Math.round(purchase.amount / purchase.credits),
                            currency,
                          ),
                        })}
                      </span>
                    )}
                  </div>
                  <Button
                    variant={isBestValue ? "default" : "secondary"}
                    size="sm"
                    className="mt-5 w-full"
                    onClick={() => onPurchase(purchase.id, name)}
                    disabled={checkoutLoading === purchase.id}
                    data-test={`credit-pack-buy-${purchase.id}`}
                  >
                    {checkoutLoading === purchase.id
                      ? t("billing:plans.loading")
                      : t("billing:packs.buy")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
